import { useState, useRef, useEffect } from "react";
import "./CSS/StickyNote.css";

//font awsome
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEyeSlash,
  faThumbTack,
} from "@fortawesome/free-solid-svg-icons";

const StickyNote = ({
  title = "Sticky Note",
  content = "Click to edit this note...",
  initialPosition = { x: 200, y: 150 },
  color = "#FFE4B5", // Default yellow sticky note color
  onPositionChange,
  onContentChange,
  baseZIndex = 100,
  onFocus,
  id,
  ExpanState = false,
}) => {
  const [position, setPosition] = useState(initialPosition);
  const [isExpanded, setIsExpanded] = useState(ExpanState);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isEditing, setIsEditing] = useState(false);
  const [noteContent, setNoteContent] = useState(content);
  const [noteTitle, setNoteTitle] = useState(title);

  const stickyRef = useRef(null);
  const textareaRef = useRef(null);
  const titleInputRef = useRef(null);

  // Handle mouse down for dragging
  const handleMouseDown = (e) => {
    if (e.target.tagName === "TEXTAREA" || e.target.tagName === "INPUT") return;

    e.preventDefault();
    e.stopPropagation();
    // Bring this sticky note to front (but still under windows)
    if (onFocus) {
      onFocus();
    }
    const rect = stickyRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    setIsDragging(true);
  };

  // Handle mouse move for dragging
  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const newPosition = {
      x: e.clientX - dragOffset.x,
      y: e.clientY - dragOffset.y,
    };

    // Keep note within viewport
    const maxX = window.innerWidth - (isExpanded ? 220 : 150);
    const maxY = window.innerHeight - (isExpanded ? 300 : 40);

    newPosition.x = Math.max(0, Math.min(newPosition.x, maxX));
    newPosition.y = Math.max(0, Math.min(newPosition.y, maxY));

    setPosition(newPosition);

    if (onPositionChange) {
      onPositionChange(id, newPosition);
    }
  };

  // Handle mouse up
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add global mouse event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  // Handle toggle expand/collapse
  const handleToggleExpand = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
    setIsEditing(false);
  };

  // Handle double click to edit
  const handleDoubleClick = (e) => {
    if (!isExpanded) return;
    e.stopPropagation();
    setIsEditing(true);
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.select();
      }
    }, 0);
  };

  // Handle content change
  const handleContentChange = (e) => {
    setNoteContent(e.target.value);
    if (onContentChange) {
      onContentChange(id, noteTitle, e.target.value);
    }
  };

  // Handle title change
  const handleTitleChange = (e) => {
    setNoteTitle(e.target.value);
    if (onContentChange) {
      onContentChange(id, e.target.value, noteContent);
    }
  };

  // Handle click outside to stop editing
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (stickyRef.current && !stickyRef.current.contains(e.target)) {
        console.log("Clicked outside sticky note");
        setIsEditing(false);
      }
    };

    if (isEditing) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isEditing]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current && isEditing) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height =
        textareaRef.current.scrollHeight + "px";
    }
  }, [noteContent, isEditing]);

  const stickyStyle = {
    position: "fixed",
    left: `${position.x}px`,
    top: `${position.y}px`,
    width: isExpanded ? "200px" : "140px",
    minHeight: isExpanded ? "250px" : "32px",
    backgroundColor: color,
    boxShadow: isDragging
      ? "0 8px 25px rgba(0,0,0,0.3), 0 0 0 2px rgba(0,0,0,0.1)"
      : "0 4px 15px rgba(0,0,0,0.2), 0 2px 5px rgba(0,0,0,0.1)",
    transform: isDragging ? "rotate(-2deg) scale(1.02)" : "rotate(-1deg)",
    transformOrigin: "center center",
    cursor: isDragging ? "grabbing" : "grab",
    userSelect: "none",
    fontFamily: "Comic Sans MS, cursive, sans-serif",
    fontSize: "14px",
    color: isDragging ? "#fff" : "#333",
    zIndex: isDragging ? Math.min(baseZIndex + 50, 149) : baseZIndex,
    transition: isDragging
      ? "none"
      : "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
    borderRadius: "0 0 5px 5px",
    //overflow: "hidden",
    // Sticky note paper texture effect
    backgroundImage: `
      linear-gradient(135deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%),
      radial-gradient(circle at 20% 20%, rgba(255,255,255,0.2) 0%, transparent 20%),
      radial-gradient(circle at 80% 80%, rgba(255,255,255,0.1) 0%, transparent 20%)
    `,
  };

  const headerStyle = {
    padding: "8px 10px",
    backgroundColor: `${color}dd`,
    borderBottom: isExpanded ? "1px solid rgba(0,0,0,0.1)" : "none",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    position: "relative",
  };

  const titleStyle = {
    fontSize: "12px",
    fontWeight: "bold",
    color: "#555",
    flex: 1,
    border: "none",
    background: "transparent",
    fontFamily: "inherit",
    outline: "none",
    cursor: "default",
  };

  const contentStyle = {
    padding: "10px",
    height: isExpanded ? "200px" : "0px",
    overflow: "hidden",
    position: "relative",
  };

  const textareaStyle = {
    width: "100%",
    height: "100%",
    border: "none",
    background: "transparent",
    resize: "none",
    outline: "none",
    fontFamily: "inherit",
    fontSize: "13px",
    color: "#333",
    lineHeight: "1.4",
    cursor: isEditing ? "text" : "default",
  };

  const toggleButtonStyle = {
    width: "25px",
    height: "50%",
    position: "absolute",
    right: "-25px",
    top: "0px",
    //top: "0px",
    backgroundColor: "rgba(0,0,0,0.1)",
    //border: "1px solid rgba(0,0,0,0.2)",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "10px",
    color: isDragging ? "#fff" : "#666",
    backdropFilter: "blur(2px)",
    transition: "all 0.2s ease",
    flexShrink: 0,
    marginLeft: "8px",
  };

  // Pin/tape effect on top
  const pinStyle = {
    position: "absolute",
    top: "-15px",
    left: "-5px",
    transform: " rotate(-20deg) scale(1.8",
    //width: "20px",
    //height: "16px",
    //backgroundColor: "#ff6b6b",
    //borderRadius: "50%",
    //boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
    zIndex: 1,
  };

  return (
    <div ref={stickyRef} style={stickyStyle} onMouseDown={handleMouseDown}>
      {/* Pin/Tape Effect */}
      <div style={pinStyle}>
        <FontAwesomeIcon icon={faThumbTack} />
      </div>

      {/* Header */}
      <div style={headerStyle}>
        <input
          ref={titleInputRef}
          type="text"
          value={noteTitle}
          onChange={handleTitleChange}
          style={titleStyle}
          onClick={(e) => e.stopPropagation()}
          onFocus={(e) => {
            e.stopPropagation();
            setIsEditing(true);
          }}
          placeholder="Note title..."
        />

        <div
          style={{
            ...toggleButtonStyle,
            borderRadius: "0 15px 15px 0",
            backgroundColor: isExpanded
              ? "rgba(255, 255, 255, 0)"
              : "rgba(255, 255, 255, 0)",
          }}
          onClick={handleToggleExpand}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = isExpanded
              ? "rgba(255, 255, 255, 0.31)"
              : "rgba(255, 255, 255, 0.31)";
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = isExpanded
              ? "rgba(255, 255, 255, 0)"
              : "rgba(255, 255, 255, 0)";
          }}
          title={isExpanded ? "Collapse" : "Expand"}
        >
          {isExpanded ? (
            <FontAwesomeIcon icon={faEyeSlash} />
          ) : (
            <FontAwesomeIcon icon={faEye} />
          )}
        </div>
      </div>

      {/* Content */}
      <div style={contentStyle}>
        <textarea
          className="custom-scroll"
          ref={textareaRef}
          value={noteContent}
          onChange={handleContentChange}
          onDoubleClick={handleDoubleClick}
          onClick={(e) => {
            e.stopPropagation();
            if (!isEditing) {
              setIsEditing(true);
            }
          }}
          style={{
            ...textareaStyle,
            cursor: isEditing ? "text" : "default",
            pointerEvents: isExpanded ? "auto" : "none",
          }}
          placeholder="Double-click to edit note..."
          readOnly={!isEditing}
        />

        {/* Ruled lines effect */}
        {isExpanded && (
          <div
            style={{
              position: "absolute",
              top: "10px",
              left: "0",
              right: "0",
              bottom: "10px",
              pointerEvents: "none",
              background: `repeating-linear-gradient(
                transparent,
                transparent 19px,
                rgba(0,0,200,0.1) 20px
              )`,
              zIndex: -1,
            }}
          />
        )}
      </div>

      {/* Corner fold effect */}
      {isExpanded && (
        <div
          style={{
            position: "absolute",
            bottom: "0",
            right: "0",
            width: "15px",
            height: "15px",
            background: `linear-gradient(-45deg, transparent 0%, transparent 40%, rgba(0,0,0,0.1) 40%, rgba(0,0,0,0.1) 100%)`,
            clipPath: "polygon(100% 0, 0 100%, 100% 100%)",
          }}
        />
      )}
    </div>
  );
};

export default StickyNote;
